import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Search, BarChart3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGES = [['home', 'Home'], ['shop', 'Shop'], ['about', 'About'], ['blog', 'Blog'], ['freebie', 'Freebie'], ['contact', 'Contact']];
const inputCls = 'w-full p-3 border border-[#E6DAC5] rounded-xl focus:border-[#3B164C] focus:outline-none';

const Counter = ({ value, max }) => <span className={`text-xs ${value.length > max ? 'text-red-600' : 'text-[#1E1622]/40'}`}>{value.length}/{max}</span>;

const PageFields = ({ k, label, page, onChange }) => (
  <div className="p-4 rounded-xl border border-[#E6DAC5]" data-testid={`seo-page-${k}`}>
    <p className="font-medium text-[#1E1622] mb-3">{label}</p>
    <div className="flex justify-between mb-1"><label className="text-xs text-[#1E1622]/60">SEO title</label><Counter value={page.title || ''} max={60} /></div>
    <input value={page.title || ''} onChange={(e) => onChange({ ...page, title: e.target.value })} placeholder="Leave blank to use the default" data-testid={`seo-title-${k}`} className={`${inputCls} mb-3`} />
    <div className="flex justify-between mb-1"><label className="text-xs text-[#1E1622]/60">Meta description</label><Counter value={page.description || ''} max={160} /></div>
    <textarea rows={2} value={page.description || ''} onChange={(e) => onChange({ ...page, description: e.target.value })} placeholder="Leave blank to use the default" data-testid={`seo-description-${k}`} className={inputCls} />
  </div>
);

export const SeoSettingsCard = ({ authHeaders }) => {
  const [seo, setSeo] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/admin/seo`, authHeaders).then((r) => setSeo(r.data)).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try { const r = await axios.put(`${API}/admin/seo`, seo, authHeaders); setSeo(r.data); toast.success('SEO & Analytics saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    setSaving(false);
  };
  if (!seo) return null;

  return (
    <div className="bg-white rounded-2xl border border-[#E6DAC5] p-6 space-y-6" data-testid="seo-settings-card">
      <div>
        <h3 className="font-semibold text-[#1E1622] flex items-center gap-2"><BarChart3 size={18} className="text-[#C5A059]" /> Google Analytics &amp; Search Console</h3>
        <p className="text-sm text-[#1E1622]/60">Tracks page views, product views, checkouts, purchases (with revenue) and sign-ups.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">GA4 Measurement ID</label>
          <input value={seo.ga_measurement_id || ''} onChange={(e) => setSeo({ ...seo, ga_measurement_id: e.target.value })} placeholder="G-XXXXXXXXXX" data-testid="seo-ga-id" className={inputCls} />
          <p className="text-xs text-[#1E1622]/50 mt-1">Google Analytics → Admin → Data streams → your website.</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Search Console verification code</label>
          <input value={seo.gsc_verification || ''} onChange={(e) => setSeo({ ...seo, gsc_verification: e.target.value })} placeholder='Paste the code or the whole <meta …> tag' data-testid="seo-gsc-code" className={inputCls} />
          <p className="text-xs text-[#1E1622]/50 mt-1">Use the "HTML tag" method. Submit sitemap: <span className="font-mono">/api/sitemap.xml</span></p>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-2">Google Ads purchase conversion label</label>
          <input value={seo.ads_conversion_label || ''} onChange={(e) => setSeo({ ...seo, ads_conversion_label: e.target.value })} placeholder="AW-18507979318/AbC123xyz" data-testid="seo-ads-label" className={inputCls} />
          <p className="text-xs text-[#1E1622]/50 mt-1">Google Ads → Goals → Conversions → your Purchase action → Tag setup → event snippet: copy the <span className="font-mono">send_to</span> value. Every paid order is then reported to Google Ads with its value.</p>
        </div>
      </div>
      <div>
        <h3 className="font-semibold text-[#1E1622] flex items-center gap-2 mb-1"><Search size={18} className="text-[#C5A059]" /> Page titles &amp; descriptions</h3>
        <p className="text-sm text-[#1E1622]/60 mb-4">Shown in Google results and link previews. Guides, packs, regions and blog posts fill theirs in automatically.</p>
        <div className="grid md:grid-cols-2 gap-4">
          {PAGES.map(([k, label]) => <PageFields key={k} k={k} label={label} page={seo.pages?.[k] || {}} onChange={(v) => setSeo({ ...seo, pages: { ...seo.pages, [k]: v } })} />)}
        </div>
      </div>
      <button onClick={save} disabled={saving} data-testid="seo-save" className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50"><Save size={18} /> {saving ? 'Saving…' : 'Save SEO & Analytics'}</button>
    </div>
  );
};
